import { inject, Injectable } from '@angular/core';
import { DossierContext } from '@shared/models/dossier-extended-dto.model';
import {
  catchError,
  from,
  map,
  mergeMap,
  Observable,
  of,
  switchMap,
  tap,
  toArray,
} from 'rxjs';
import { ArrayUtil } from '@shared/utils/array.util';
import { DossierService } from './cabinet/dossier.service';
import { CollaborateurService } from './cabinet/collaborateur.service';
import { PreferenceService } from './cabinet/preference.service';

@Injectable({ providedIn: 'root' })
export class DossierResolverService {
  private readonly dossierService = inject(DossierService);
  private readonly collaborateurService = inject(PreferenceService);

  resolve(): Observable<DossierContext[]> {
    return of(this.dossierService.getDossierAccessRights()).pipe(
      map((dossierInfos) => {
        if (this.collaborateurService.asUser) {
          return dossierInfos.filter(
            (d) => d.interlocuteurId === this.collaborateurService.asUser,
          );
        }
        return dossierInfos;
      }),
      switchMap((dossierInfos) =>
        from(dossierInfos).pipe(
          mergeMap((dossierInfo) => {
            // Modifie le cabinet courant à celui associé au dossier pour l'appel à l'API.
            this.dossierService.storeCabinetId(dossierInfo.cabinetId);
            this.dossierService.setCurrentDossierId(dossierInfo.id);
            return this.dossierService
              .get(dossierInfo.id, {
                dossierId: dossierInfo.id,
                cabinetId: dossierInfo.cabinetId,
              })
              .pipe(
                map((dossier) => {
                  return {
                    ...dossierInfo,
                    ...dossier,
                  } as DossierContext;
                }),
              );
          }),
          toArray(),
        ),
      ),
      tap((dossiers: DossierContext[]) => {
        this.dossierService.dossiers = dossiers.sort((d1, d2) =>
          ArrayUtil.sortByValue(
            d1.intervenant.raisonSociale,
            d2.intervenant.raisonSociale,
          ),
        );
      }),
      catchError(() => {
        console.error('Erreur lors de la récupération des dossiers.');
        return of([]);
      }),
    );
  }
}
